import {useEffect, useState} from "react"

import {
    NavLink,
    Route,
    Switch,
    useParams,
    useRouteMatch,
} from "react-router-dom"

// import classnames from "classnames"

import { useFirebase } from "@meddbriefer/mdb-firebase"

import { SYSTEMS } from "@meddbriefer/scenario-data/constants.js"
import { getScenarioTypeByID } from "@meddbriefer/scenario-data/index.js"

import { ScenarioTypeContext } from "./ScenarioTypeContext"
import { useHeader } from "../HeaderContext"

import CheckList from "./CheckListEditor/CheckList"
import Medications from "./MedicationsEditor/Medications"
import InterventionsEditor from "./InterventionsEditor"

import { appendObjToArray, updateObjInArray, removeObjWithIdFromArray } from "./utils"

const COLLECTION_ID = "scenario-types"

export default function ScenarioType(props) {
    const {db} = useFirebase()
    const params =  useParams()
    const {scenarioTypeID} = params
    const [scenarioType, setScenarioType] = useState(null)
    const [uniqueIDs, setUniqueIDs] = useState(null)
    const [uniqueLabels, setUniqueLabels] = useState(null)
    const {path} = useRouteMatch()
    const {setTitle} = useHeader()

    // wrapper around setScenarioType() to only update a specified fldName/fldVal
    const updateScenTypeFld = (fldName, fldVal) => setScenarioType(prevState => ({ ...prevState, [fldName]: fldVal }))

    // objects containing functions to update/access specific portions of scenarioType state
    const intvs = {
        add: (newObj) => updateScenTypeFld("interventions", appendObjToArray(scenarioType.interventions, newObj)),
        update: (newObj) => updateScenTypeFld("interventions", updateObjInArray(scenarioType.interventions, newObj)),
        remove: (id) => updateScenTypeFld("interventions", removeObjWithIdFromArray(scenarioType.interventions, id)),
        getSorted: () => {
            const sortByLabel = (a, b) => a.label.localeCompare(b.label)
            let sorted = []
            const order = [SYSTEMS.AW, SYSTEMS.BR, SYSTEMS.BL, SYSTEMS.SH, SYSTEMS.OTH, undefined]
            order.forEach(sys => {
                sorted = sorted.concat(scenarioType.interventions.filter(e => e.system === sys).sort(sortByLabel))
            })
            return sorted
        }
    }

    const intvPrompts = {
        add: (newObj) => updateScenTypeFld("interventionPrompts", appendObjToArray(scenarioType.interventionPrompts, newObj)),
        update: (newObj) => updateScenTypeFld("interventionPrompts", updateObjInArray(scenarioType.interventionPrompts, newObj)),
        remove: (id) => updateScenTypeFld("interventionPrompts", removeObjWithIdFromArray(scenarioType.interventionPrompts, id))
    }

    const intvPromptAnswers = {
        add: (newObj) => updateScenTypeFld("interventionPromptAnswers", appendObjToArray(scenarioType.interventionPromptAnswers, newObj)),
        update: (newObj) => updateScenTypeFld("interventionPromptAnswers", updateObjInArray(scenarioType.interventionPromptAnswers, newObj)),
        remove: (id) => updateScenTypeFld("interventionPromptAnswers", removeObjWithIdFromArray(scenarioType.interventionPromptAnswers, id))
    }

    const intvMenuCategories = {
        add: (newObj) => updateScenTypeFld("interventionMenuCategories", appendObjToArray(scenarioType.interventionMenuCategories, newObj)),
        update: (newObj) => updateScenTypeFld("interventionMenuCategories", updateObjInArray(scenarioType.interventionMenuCategories, newObj)),
        remove: (id) => updateScenTypeFld("interventionMenuCategories", removeObjWithIdFromArray(scenarioType.interventionMenuCategories, id)),
        reorder: (newArray) => updateScenTypeFld("interventionMenuCategories", newArray)
    }

    const intvMenuItems = {
        add: (newObj) => updateScenTypeFld("interventionMenuItems", appendObjToArray(scenarioType.interventionMenuItems, newObj)),
        update: (newObj) => updateScenTypeFld("interventionMenuItems", updateObjInArray(scenarioType.interventionMenuItems, newObj)),
        remove: (id) => updateScenTypeFld("interventionMenuItems", removeObjWithIdFromArray(scenarioType.interventionMenuItems, id))
    }

    const saveCheckList = (metaData) => {
        // , hierarchy
        // filter out fields with 'undefined' as a value
        const cleanedMD = JSON.parse(JSON.stringify(metaData))

        setScenarioType(prevState => (
            {
                ...prevState,
                checklist: cleanedMD,
                // phaseData: hierarchy
            }
        ))
        return db.collection(COLLECTION_ID).doc(scenarioTypeID).update({
            checklist: cleanedMD,
            // phaseData: hierarchy
        })
        .then(() => true)
        .catch(error => {
            alert("an error occured saving scenario-type changes")
            console.log(error)
            return false
        })
    }

    const saveMedications = (data) => {
        setScenarioType(prevState => (
            {...prevState, medications: data}
        ))
        return db.collection(COLLECTION_ID).doc(scenarioTypeID).update({
            medications: data
        })
        .then(() => true)
        .catch(error => {
            console.log(error)
            return false
        })
    }

    const saveInterventions = () => {
        // saves all interventions related state
        return db.collection(COLLECTION_ID).doc(scenarioTypeID).update({
            interventions: scenarioType.interventions,
            interventionPrompts: scenarioType.interventionPrompts,
            interventionPromptAnswers: scenarioType.interventionPromptAnswers,
            interventionMenuCategories: scenarioType.interventionMenuCategories,
            interventionMenuItems: scenarioType.interventionMenuItems
        })
        .then(() => true)
        .catch(error => {
            console.log(error)
            return false
        })
    }

    useEffect(() => {
        if (scenarioTypeID) {
            getScenarioTypeByID(db, scenarioTypeID)
            .then(data => {
                setScenarioType(data)
                setTitle(`Scenario Type: ${data.name}`)
            })
        }
    }, [db, scenarioTypeID, setTitle])

    useEffect(
        () => {
            if (scenarioType) {
                // all objects must have uniqueIDs
                let ids = scenarioType.interventions.map(e => e.id)
                ids = ids.concat(scenarioType.interventionPrompts.map(e => e.id))
                ids = ids.concat(scenarioType.interventionPromptAnswers.map(e => e.id))
                ids = ids.concat(scenarioType.interventionMenuCategories.map(e => e.id))
                ids = ids.concat(scenarioType.interventionMenuItems.map(e => e.id))
                ids = ids.concat(scenarioType.medications.map(e => e.id))
                ids = ids.concat(scenarioType.checklist.map(e => e.id))
                // console.log(ids)
                setUniqueIDs(ids)
                // only interventions and checklist items require unique labels (searchable label -> id)
                let labels = scenarioType.interventions.map(e => e.label)
                labels = labels.concat(scenarioType.checklist.map(e => e.label))
                // console.log(labels)
                setUniqueLabels(labels)
            }
        }, [scenarioType]
    )

    if (!scenarioType) {
        return <div>Loading...</div>
    } else {
        window.scenarioType = scenarioType
    }

    return (
        <div className="scenario-type-editor">
            <nav id="tab-selector">
                <NavLink
                    to={`/scenario-type/${scenarioTypeID}/checklist`}
                    className="tab-button"
                    activeClassName="active-tab"
                >
                    Checklist
                </NavLink>
                <NavLink
                    to={`/scenario-type/${scenarioTypeID}/medications`}
                    className="tab-button"
                    activeClassName="active-tab"
                >
                    Medications
                </NavLink>
                <NavLink
                    to={`/scenario-type/${scenarioTypeID}/interventions`}
                    className="tab-button"
                    activeClassName="active-tab"
                >
                    Interventions
                </NavLink>
            </nav>
            <ScenarioTypeContext.Provider
                value={{
                    scenarioType,
                    uniqueIDs,
                    uniqueLabels,
                    intvs,
                    intvPrompts,
                    intvPromptAnswers,
                    intvMenuCategories,
                    intvMenuItems,
                    saveCheckList,
                    saveMedications,
                    saveInterventions,
                }}
            >
                <Switch>
                    <Route path={`${path}/checklist`} component={CheckList} />
                    <Route path={`${path}/medications`} component={Medications} />
                    <Route path={`${path}/interventions`} component={InterventionsEditor} />
                    <Route exact path={path}>
                        <div>Click on one of the tabs above to make changes.</div>
                    </Route>
                </Switch>
            </ScenarioTypeContext.Provider>
        </div>
    )
}
